import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  Button,
  List,
  Spin,
  Card,
  Switch,
  Flex,
  Popover,
  Slider,
  InputNumber,
} from "antd";
import {
  useCreateChecklistMutation,
  useUpdateChecklistMutation,
  useFetchChecklistsByTypeQuery,
  useDeleteChecklistMutation,
  useReorderTasksMutation,
} from "../opencheck/checklists.service"; // Updated import
import { CheckOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import "./style.css";
import TextEditor from "../../components/TextEditor";

const HotholdChecklistCreate = () => {
  const { t, i18n } = useTranslation();
  const curLanguage = i18n.language;
  const navigate = useNavigate();

  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [newChecklist, setNewChecklist] = useState("");
  const [isInput, setIsInput] = useState(false);
  const [record, setRecord] = useState("");
  const [isTempSlider, setIsTempSlider] = useState(false);
  const [checklistData, setChecklistData] = useState([]);

  const {
    data: checklists = [],
    isFetching,
    isSuccess,
  } = useFetchChecklistsByTypeQuery("hothold", {
    refetchOnMountOrArgChange: true,
  });

  const [createChecklist, { isLoading: isCreateLoading }] =
    useCreateChecklistMutation();
  const [updateChecklist] = useUpdateChecklistMutation();
  const [deleteChecklist] = useDeleteChecklistMutation();
  const [reorderTasks] = useReorderTasksMutation();

  const handleCompleteChecklist = async (data) => {
    try {
      await updateChecklist(data);
    } catch (error) {
      console.log("Failed to complete checklist");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const checklistTempData = checklists.reduce((arr, cur) => {
        arr.push({
          isSection: cur.isSection,
          _id: cur._id,
          taskNumber: cur.taskNumber,
          title: cur.isSection
            ? ""
            : cur[`title_${curLanguage}`] || cur[`title_en`],
          description: cur.isSection ? (
            <div
              style={{ paddingBottom: "10px" }}
              dangerouslySetInnerHTML={{
                __html: cur[`title_${curLanguage}`] || cur[`title_en`],
              }}
            ></div>
          ) : (
            <div className="opening-check-add-task">
              {cur.isInput && (
                <div style={{ padding: "5px 15px" }}>
                  <Input style={{ background: "transparent" }} />
                </div>
              )}
              {cur.isTempSlider && (
                <>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      textAlign: "center",
                      marginTop: 10,
                      fontWeight: "bold",
                    }}
                  >
                    {cur.tempSlider.toFixed(1)}
                    °C
                  </div>
                  <Slider
                    style={{ margin: "0 15px" }}
                    min={-30}
                    max={120}
                    step={0.1}
                    value={cur.tempSlider}
                  />
                </>
              )}
              {cur.isDateRecord && (
                <div>*RECORD TIME AND DATE AUTOMATICALLY*</div>
              )}
              {cur.isTemperature && (
                <div>*RECORD FRIDGE TEMPERATURES AUTOMATICALLY*</div>
              )}
              {cur.status === "completed" ? (
                <div style={{ textAlign: "center", padding: "5px" }}>
                  <CheckOutlined className="checklist-complete" />
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "5px" }}>
                  <CheckOutlined
                    className="checklist-pending"
                    onClick={() =>
                      handleCompleteChecklist({
                        id: cur._id,
                        data: { status: "completed" },
                      })
                    }
                  />
                </div>
              )}
            </div>
          ),
        });
        return arr;
      }, []);
      setChecklistData(checklistTempData);
    }
  }, [checklists]);

  const handleAddTask = async () => {
    try {
      if (!newChecklist) {
        toast.error("Checklist title cannot be empty");
        return;
      }
      await createChecklist({
        title: newChecklist,
        type: "hothold",
        isInput,
        status: "pending",
        isTemperature: record === "recordTemp",
        isDateRecord: record === "recordTD",
        isTempSlider,
      });
      setNewChecklist("");
      setIsInput(false);
      setRecord("");
    } catch (error) {
      console.log("Failed to create checklist");
    }
  };

  const handleAddSection = async () => {
    try {
      if (!newChecklist) {
        toast.error("Checklist title cannot be empty");
        return;
      }
      await createChecklist({
        title: newChecklist,
        type: "hothold",
        isInput: false,
        status: "pending",
        isTemperature: false,
        isDateRecord: false,
        isSection: true,
      });
      setNewChecklist("");
      setIsInput(false);
      setRecord("");
    } catch (error) {
      console.log("Failed to create checklist");
    }
  };

  const handleReorderTask = (orderTask, type) => {
    const shift = type === "up" ? -1 : 1;
    const targetTask = checklists.find(
      (item) => item.taskNumber === orderTask.taskNumber + shift
    );
    if (targetTask) {
      const reordered = checklists.map((item) => {
        if (item._id === targetTask._id) {
          return { id: item._id, taskNumber: item.taskNumber - shift };
        }
        if (item._id === orderTask._id) {
          return { id: item._id, taskNumber: item.taskNumber + shift };
        }
        return { id: item._id, taskNumber: item.taskNumber };
      });
      reorderTasks({ tasks: reordered });
    }
  };

  return (
    <Spin spinning={isFetching}>
      <Card
        title={t("PROBE CHECKLIST CREATE")}
        bordered={false}
        className="opencheck-create-page"
        extra={
          <Flex justify="center">
            <Button type="default" onClick={() => navigate("/provechecks")}>
              {t("BACK")}
            </Button>
          </Flex>
        }
      >
        <div className="opencheck-create-page-container">
          <TextEditor
            newChecklist={newChecklist}
            setNewChecklist={setNewChecklist}
          />
          <div className="opencheck-create-page-container-switch">
            <h4>{t("ADD TEXTBOX")}</h4>
            <Switch value={isInput} onChange={setIsInput} />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("RECORD TEMPERATURES")}</h4>
            <Switch
              value={record === "recordTemp"}
              onChange={(e) => setRecord(e ? "recordTemp" : "")}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("RECORD TIME & DATE")}</h4>
            <Switch
              value={record === "recordTD"}
              onChange={(e) => setRecord(e ? "recordTD" : "")}
            />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("ADD TEMPERATURE SLIDER")}</h4>
            <Switch value={isTempSlider} onChange={(e) => setIsTempSlider(e)} />
          </div>
          <div className="opencheck-create-page-container-button">
            <Button loading={isCreateLoading} onClick={handleAddTask}>
              {t("Add task to checklist")}
            </Button>
            <Button loading={isCreateLoading} onClick={handleAddSection}>
              {t("Add Section")}
            </Button>
          </div>
        </div>

        <List
          className="opencheck-create-page-insight"
          itemLayout="horizontal"
          dataSource={checklistData}
          renderItem={(checklist) => (
            <List.Item
              className={
                checklist.title === ""
                  ? "background-section"
                  : "background-task"
              }
              extra={
                <Popover
                  placement="bottom"
                  content={
                    <div className="opencheck-list-page-tools-popover">
                      <Button
                        style={{ background: "rgb(47, 45, 113)" }}
                        onClick={() => {
                          handleReorderTask(checklist, "up");
                          setOpenPopoverId(null);
                        }}
                      >
                        {t("MoveUp")}
                      </Button>
                      <Button
                        style={{ background: "rgb(47, 45, 113)" }}
                        onClick={() => {
                          handleReorderTask(checklist, "down");
                          setOpenPopoverId(null);
                        }}
                      >
                        {t("MoveDown")}
                      </Button>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          deleteChecklist(checklist._id);
                          setOpenPopoverId(null);
                        }}
                      >
                        {t("DELETE")}
                      </Button>
                    </div>
                  }
                  open={openPopoverId === checklist._id}
                  onOpenChange={(visible) =>
                    setOpenPopoverId(visible ? checklist._id : null)
                  }
                  trigger="click"
                >
                  <MoreOutlined className="opencheck-list-page-tools-icon" />
                </Popover>
              }
            >
              <List.Item.Meta
                title={
                  checklist.title !== "" ? (
                    <div
                      className="compliance-checklist-content"
                      dangerouslySetInnerHTML={{ __html: checklist.title }}
                    ></div>
                  ) : (
                    ``
                  )
                }
                description={checklist.description}
              />
            </List.Item>
          )}
        />
      </Card>
    </Spin>
  );
};

export default HotholdChecklistCreate;
